import { supabase } from "@/integrations/supabase/client";
import { ServiceError, withServiceErrorHandling } from '@/lib/service-error';
import { logger } from '@/lib/structured-logger';
import type { Database } from '@/integrations/supabase/types';

type Json = Database['public']['Tables']['activity_log']['Insert']['metadata'];

export interface LogActivityParams {
  userId: string;
  organizationId: string;
  actionType: string;
  entityType: string;
  entityId: string;
  metadata?: Record<string, unknown>;
}

export interface ActivityLogEntry {
  id: string;
  user_id: string;
  organization_id: string;
  action_type: string;
  entity_type: string;
  entity_id: string;
  metadata: Record<string, unknown>;
  created_at: string;
  profiles?: {
    full_name: string | null;
    email: string;
  };
}

export interface ActivityFilters {
  actionType?: string;
  entityType?: string;
  userId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export class ActivityService {
  private static readonly TABLE = 'activity_log';
  private static logger = logger.child({ component: 'ActivityService' });

  /**
   * Fetch activity log for an organization
   */
  static async getActivityLog(
    organizationId: string,
    limit: number = 50
  ): Promise<ActivityLogEntry[]> {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching activity log', { organizationId, limit });

      const { data, error } = await supabase
        .from(this.TABLE)
        .select("*")
        .eq("organization_id", organizationId)
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) {
        this.logger.error('Failed to fetch activity log', error, { organizationId });
        throw error;
      }

      return (data || []) as ActivityLogEntry[];
    }, { operation: 'getActivityLog', table: this.TABLE });
  }

  /**
   * Log a new activity
   * Fire-and-forget operation - won't block if it fails
   */
  static async logActivity(params: LogActivityParams): Promise<void> {
    try {
      this.logger.debug('Logging activity', { 
        actionType: params.actionType,
        entityType: params.entityType 
      });

      const { error } = await supabase.from(this.TABLE).insert({
        user_id: params.userId,
        organization_id: params.organizationId,
        action_type: params.actionType,
        entity_type: params.entityType,
        entity_id: params.entityId,
        metadata: (params.metadata || null) as Json,
      });

      if (error) {
        this.logger.error('Failed to log activity', error, { params });
      }
    } catch (error) {
      // Silent fail - activity logging shouldn't break the app
      this.logger.error('Activity logging error', error as Error, { params });
    }
  }

  /**
   * Get activity feed for organization with filters
   */
  static async getActivityFeed(
    organizationId: string,
    filters: ActivityFilters = {}
  ): Promise<{ activities: ActivityLogEntry[]; total: number }> {
    return withServiceErrorHandling(async () => {
      let query = supabase
        .from(this.TABLE)
        .select("*", { count: "exact" })
        .eq("organization_id", organizationId);

      // Apply filters
      if (filters.actionType) {
        query = query.eq("action_type", filters.actionType);
      }
      if (filters.entityType) {
        query = query.eq("entity_type", filters.entityType);
      }
      if (filters.userId) {
        query = query.eq("user_id", filters.userId);
      }
      if (filters.startDate) {
        query = query.gte("created_at", filters.startDate);
      }
      if (filters.endDate) {
        query = query.lte("created_at", filters.endDate);
      }

      // Pagination
      const limit = filters.limit || 50;
      const offset = filters.offset || 0;
      query = query.order("created_at", { ascending: false }).range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      if (error) throw error;

      // Fetch profile data separately
      const userIds = [...new Set(data?.map(d => d.user_id) || [])];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", userIds);

      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      const activities = data?.map(activity => ({
        ...activity,
        profiles: profileMap.get(activity.user_id) || null
      })) as ActivityLogEntry[];

      return {
        activities: activities || [],
        total: count || 0,
      };
    }, { operation: 'getActivityFeed', table: this.TABLE });
  }

  /**
   * Get recent activities (last 10)
   */
  static async getRecentActivities(
    organizationId: string
  ): Promise<ActivityLogEntry[]> {
    const { activities } = await this.getActivityFeed(organizationId, {
      limit: 10,
    });
    return activities;
  }

  /**
   * Get activity counts by type for dashboard
   */
  static async getActivityStats(
    organizationId: string,
    days: number = 7
  ): Promise<Record<string, number>> {
    return withServiceErrorHandling(async () => {
      const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

      const { data, error } = await supabase
        .from(this.TABLE)
        .select("action_type")
        .eq("organization_id", organizationId)
        .gte("created_at", startDate);

      if (error) throw error;

      const stats: Record<string, number> = {};
      data?.forEach((activity) => {
        stats[activity.action_type] = (stats[activity.action_type] || 0) + 1;
      });

      return stats;
    }, { operation: 'getActivityStats', table: this.TABLE });
  }

  /**
   * Format action type for display
   */
  static formatActionType(actionType: string): string {
    const formatMap: Record<string, string> = {
      post_created: "Post Created",
      post_submitted: "Submitted for Approval",
      post_approved: "Post Approved",
      post_rejected: "Post Rejected",
      post_published: "Post Published",
      post_deleted: "Post Deleted",
      campaign_created: "Campaign Created",
      campaign_updated: "Campaign Updated",
      brand_created: "Brand Created",
      brand_updated: "Brand Updated",
    };

    return formatMap[actionType] || actionType.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  }

  /**
   * Get action icon for activity type
   */
  static getActionIcon(actionType: string): string {
    const iconMap: Record<string, string> = {
      post_created: "FileText",
      post_submitted: "Send",
      post_approved: "CheckCircle",
      post_rejected: "XCircle",
      post_published: "Upload",
      post_deleted: "Trash2",
      campaign_created: "Folder",
      campaign_updated: "Edit",
      brand_created: "Tag",
      brand_updated: "Edit",
    };

    return iconMap[actionType] || "Activity";
  }
}
